import { createFileRoute, notFound, type SearchSchemaInput } from '@tanstack/react-router';
import { canUseMockMode } from '@/lib/mock-mode';
import { InsightsPage } from '@/features/admin/insights/insights-page';
import { activityReportQuery, infraReportQuery, spendReportQuery, type SpendWindow } from '@/services/insights-admin';

// The service's SPEND_WINDOWS, written out: validateSearch ships in the entry chunk, which must not load the service
// (it brings zod to the sign-in page).
const windowOf = (value: unknown): SpendWindow => {
  const n = Number(value);
  return n === 1 || n === 30 ? n : 7;
};
type Tab = 'activity' | 'health' | 'spend';
const tabOf = (value: unknown): Tab => (value === 'health' || value === 'spend' ? value : 'activity');

// Dreamwell staff only: anyone else gets the not-found page (the API refuses them too). `days` is the window, `tab`
// the section shown.
export const Route = createFileRoute('/_authed/admin/insights')({
  validateSearch: (search: { days?: unknown; tab?: unknown } & SearchSchemaInput) => ({ days: windowOf(search.days), tab: tabOf(search.tab) }),
  beforeLoad: ({ context: { user } }) => {
    if (!canUseMockMode(user.email)) throw notFound();
  },
  loaderDeps: ({ search }) => ({ days: search.days }),
  // A refused or failed read is shown by the page itself (its own error card), never as a broken route.
  loader: ({ context: { queryClient }, deps }) =>
    Promise.all([
      queryClient.ensureQueryData(activityReportQuery(deps.days)),
      queryClient.ensureQueryData(infraReportQuery(deps.days)),
      queryClient.ensureQueryData(spendReportQuery(deps.days)),
    ]).catch(() => null),
  component: InsightsRoute,
});

function InsightsRoute() {
  const { team } = Route.useRouteContext();
  const { days, tab } = Route.useSearch();
  const navigate = Route.useNavigate();
  if (!team) return null;
  return (
    <InsightsPage
      teamId={team.id}
      days={days}
      tab={tab}
      onDays={(next) => void navigate({ search: (s) => ({ ...s, days: next }), replace: true })}
      onTab={(next) => void navigate({ search: (s) => ({ ...s, tab: next }), replace: true })}
    />
  );
}
